import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PaperPlaneTilt, MagnifyingGlass } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { byProductName } from "../lib/utils";
import { useAuth } from "../AuthContext";
import { BrutalButton, BrutalInput } from "./Brutal";

const VIALS_PER_BOX = 10;

// v405.10 Bulk Give — move MANY products to ONE distributor in a single
// confirm (Svitlana hosting the Ontario inventory). Quantities are entered in
// boxes (x10 = vials) or raw units; every transfer is a standard-wholesale
// consignment, identical to the per-card Give.
export const BulkGiveDialog = ({ products, onClose, onDone, initial }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [distributors, setDistributors] = useState([]);
  const [distId, setDistId] = useState(initial?.distId || "");
  const [inBoxes, setInBoxes] = useState(initial?.qty ? false : true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({}); // product_id -> qty string
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/distributors").then(({ data }) => setDistributors(data || [])).catch(() => {});
  }, []);

  // v406.7 — email deep link prefill: match the requested product by name and
  // pre-select it (with the requested qty in vials when known).
  useEffect(() => {
    if (!initial?.productName) return;
    const target = (initial.productName || "").trim().toLowerCase();
    const p = products.find((x) => (x.name || "").trim().toLowerCase() === target);
    if (p) setSel({ [p.id]: initial.qty ? String(initial.qty) : "2" });
    // eslint-disable-next-line
  }, [initial?.productName, products.length]);

  const factor = inBoxes ? VIALS_PER_BOX : 1;
  const rows = useMemo(
    () =>
      products
        .filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()))
        .sort(byProductName),
    [products, q]
  );
  const picked = products
    .map((p) => ({ p, units: Math.round((Number(sel[p.id]) || 0) * factor) }))
    .filter((x) => x.units > 0);
  const overStock = picked.filter((x) => x.units > (x.p.current_stock || 0));
  const totalUnits = picked.reduce((s, x) => s + x.units, 0);
  const distName = distributors.find((d) => d.id === distId)?.name || "";
  const canSubmit = distId && picked.length > 0 && overStock.length === 0 && !busy;

  const toggle = (p) =>
    setSel((s) => (s[p.id] !== undefined ? (({ [p.id]: _, ...rest }) => rest)(s) : { ...s, [p.id]: "2" }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/products/bulk-assign", {
        distributor_id: distId,
        items: picked.map((x) => ({ product_id: x.p.id, quantity: x.units })),
        note: "Bulk Give",
      });
      if (data.ok_count > 0) toast.success(t("bulk_give.success", { n: data.ok_count, name: distName }));
      if (data.fail_count > 0) {
        toast.error(t("bulk_give.partial", { n: data.fail_count }), {
          description: data.results.filter((r) => !r.ok).map((r) => `${r.name}: ${r.error}`).join("\n"),
          duration: 10000,
        });
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border-2 border-[#c9a24a] rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-give-dialog"
      >
        <div className="p-5 pb-3">
          <div className="flex items-center gap-2">
            <PaperPlaneTilt size={22} weight="bold" className="text-[#8a6a1c]" />
            <h2 className="text-xl font-semibold">{t("bulk_give.title")}</h2>
          </div>
          <p className="text-sm text-zinc-600 mt-1">{t("bulk_give.subtitle")}</p>
          <div className="flex flex-wrap items-end gap-3 mt-3">
            <div className="flex-1 min-w-[190px]">
              <label className="block text-xs font-semibold mb-1">{t("bulk_give.destination")}</label>
              <select
                value={distId}
                onChange={(e) => setDistId(e.target.value)}
                className="input-brutal h-11 !py-0 pr-8 w-full font-medium"
                data-testid="bulk-give-dist-select"
              >
                <option value="">{t("bulk_give.pick_destination")}</option>
                {distributors
                  // v409.2 — central inventory: only the giver's own personal
                  // entry is a valid bulk destination.
                  .filter((d) => !d.is_personal || d.owner_id === user?.id)
                  .sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("bulk_give.count_in")}</label>
              <div className="flex rounded-lg border border-zinc-300 overflow-hidden" data-testid="bulk-give-unit-toggle">
                {[[true, t("bulk_give.boxes")], [false, t("bulk_give.vials")]].map(([val, label]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setInBoxes(val)}
                    className={`px-3 h-11 text-sm font-semibold ${inBoxes === val ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {inBoxes && <p className="text-[11px] text-zinc-500 mt-1.5">{t("bulk_give.box_hint")}</p>}
          <div className="relative mt-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("bulk_give.search")}
              className="input-brutal h-10 !py-0 pl-9 w-full text-sm"
              data-testid="bulk-give-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto border-t border-zinc-200 divide-y divide-zinc-100" data-testid="bulk-give-list">
          {rows.map((p) => {
            const checked = sel[p.id] !== undefined;
            const units = Math.round((Number(sel[p.id]) || 0) * factor);
            const over = checked && units > (p.current_stock || 0);
            return (
              <div key={p.id} className={`flex items-center gap-3 px-4 py-2.5 ${checked ? "bg-[#fbf6e7]/60" : ""}`} data-testid={`bulk-give-row-${p.id}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p)}
                  className="w-5 h-5 accent-[#8a6a1c] shrink-0"
                  data-testid={`bulk-give-check-${p.id}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className={`text-xs ${over ? "text-red-600 font-semibold" : "text-zinc-500"}`}>
                    {t("bulk_give.in_stock", { n: p.current_stock, unit: p.unit || "units" })}
                    {over && ` — ${t("bulk_give.exceeds")}`}
                  </p>
                </div>
                {checked && (
                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      type="number"
                      min="0"
                      value={sel[p.id]}
                      onChange={(e) => setSel((s) => ({ ...s, [p.id]: e.target.value }))}
                      className={`input-brutal h-10 !py-0 w-20 text-center font-semibold ${over ? "!border-red-500" : ""}`}
                      data-testid={`bulk-give-qty-${p.id}`}
                    />
                    {inBoxes && <span className="text-xs text-zinc-500 tabular-nums w-14">= {units} {p.unit || "vials"}</span>}
                  </div>
                )}
              </div>
            );
          })}
          {rows.length === 0 && <p className="px-4 py-4 text-sm text-zinc-500">{t("bulk_give.no_match")}</p>}
        </div>

        <div className="p-4 border-t border-zinc-200 space-y-3">
          {picked.length > 0 && distId && (
            <p className="text-sm font-semibold text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a] rounded-lg px-3 py-2" data-testid="bulk-give-summary">
              {t("bulk_give.summary", { items: picked.length, units: totalUnits, name: distName })}
            </p>
          )}
          <div className="flex gap-3">
            <BrutalButton variant="secondary" className="flex-1" onClick={onClose} data-testid="bulk-give-cancel">
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton className="flex-1" disabled={!canSubmit} onClick={submit} data-testid="bulk-give-confirm">
              <PaperPlaneTilt size={16} weight="bold" />
              {busy ? t("common.saving") : t("bulk_give.confirm")}
            </BrutalButton>
          </div>
        </div>
      </div>
    </div>
  );
};
